import React, { useEffect, useState } from "react";
import SidebarLayout from "../SidebarLayout";
import {
  Container,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import axiosInstance from "../AxiosInstance";
import CourseTable from "../course/CourseTable";
import { useNotification } from "../NotificationContext";

const RepostMain = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();
  const [reusableOpen, setReusableOpen] = useState(false);
  const [reusableList, setReusableList] = useState([]);
  const [selectedCancelledId, setSelectedCancelledId] = useState(null);
  const [currentRepost, setCurrentRepost] = useState(null);

  const fetchRepostCourses = () => {
    if (isLoading) return;
    setIsLoading(true);
    axiosInstance
      .get("/course", {
        params: {
          pageNum: 1,
          pageSize: 500,
          courseStatus: 3, // Repost Required
          isActive: false, // AD not published
        },
      })
      .then((res) => {
        if (res.data.message === "success") {
          setCourses(res.data.data ? res.data.data.items : []);
        } else {
          showNotification("Failed to load repost tasks", "error");
        }
      })
      .catch(() => {
        showNotification("Failed to load repost tasks", "error");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchRepostCourses();
  }, []);

  const openReusableDialog = (course) => {
    setCurrentRepost(course);
    setSelectedCancelledId(null);
    setReusableOpen(true);
    axiosInstance
      .get("/course/reusable", { params: { repostId: course.id } })
      .then((res) => setReusableList(res.data.data || []))
      .catch(() => setReusableList([]));
  };

  return (
    <SidebarLayout>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Repost</Typography>
        </Box>
        <CourseTable
          currentItems={courses}
          handleClickOpen={undefined} // hide edit
          showMarkProcessed
          onMarkProcessed={openReusableDialog}
        />

        <Dialog
          open={reusableOpen}
          onClose={() => setReusableOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Ads can be reutilized</DialogTitle>
          <DialogContent>
            {reusableList.length === 0 && (
              <Typography>No cancelled ads available for reuse</Typography>
            )}
            {reusableList.length > 0 && (
              <RadioGroup
                value={selectedCancelledId || ""}
                onChange={(e) => setSelectedCancelledId(Number(e.target.value))}
              >
                <List>
                  {reusableList.map((item) => (
                    <ListItem key={item.id} divider>
                      <FormControlLabel
                        value={item.id}
                        control={<Radio />}
                        label={
                          <ListItemText
                            primary={`${item.courseTitle || ""} - ${item.address || ""}`}
                            secondary={`Date: ${item.date}`}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </RadioGroup>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReusableOpen(false)}>Cancel</Button>
            {reusableList.length === 0 && (
              <Button
                onClick={() => {
                  if (!currentRepost) return;
                  const payload = {
                    ...currentRepost,
                    active: true,
                    courseStatus: 3,
                  };
                  axiosInstance
                    .put("/course", payload, {
                      headers: { "Content-Type": "application/json" },
                    })
                    .then((res) => {
                      if (res.data.message === "success") {
                        showNotification("Published without reuse", "success");
                        setReusableOpen(false);
                        fetchRepostCourses();
                      } else {
                        showNotification("Publish failed", "error");
                      }
                    })
                    .catch(() => showNotification("Publish failed", "error"));
                }}
              >
                Publish without reuse
              </Button>
            )}
            <Button
              onClick={() => {
                if (!currentRepost || !selectedCancelledId) {
                  showNotification(
                    "Please select a cancelled ad to reuse",
                    "warning",
                  );
                  return;
                }
                axiosInstance
                  .post(
                    "/course/reuse",
                    {},
                    {
                      params: {
                        repostId: currentRepost.id,
                        cancelledId: selectedCancelledId,
                      },
                    },
                  )
                  .then((res) => {
                    if (res.data.message === "success") {
                      showNotification("Reused successfully", "success");
                      setReusableOpen(false);
                      fetchRepostCourses();
                    } else {
                      showNotification(
                        res.data.message || "Reuse failed",
                        "error",
                      );
                    }
                  })
                  .catch((err) => {
                    const msg = err?.response?.data?.message || "Reuse failed";
                    showNotification(msg, "error");
                  });
              }}
              disabled={!selectedCancelledId}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </SidebarLayout>
  );
};

export default RepostMain;
